import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import type { StyleProp, ViewStyle } from "react-native";
import { Menu, Text } from "react-native-paper";

import { colors, radius, spacing, typography } from "@/lib/theme";

export interface MenuSelectOption {
    label: string;
    value: string;
}

interface MenuSelectProps {
    value: string | undefined;
    options: MenuSelectOption[];
    onChange: (value: string) => void;
    placeholder?: string;
    readOnly?: boolean;
    disabled?: boolean;
    style?: StyleProp<ViewStyle>;
}

// Cap the open menu so a long list (e.g. 100 years) scrolls instead of
// overflowing the screen. Paper's Menu doesn't scroll its items on its own.
const MENU_MAX_HEIGHT = 300;

/**
 * A single-select dropdown built on Paper's Menu. Replaces the radio-list
 * rendering for `dropdown` items and backs the date picker's month/day/year
 * selectors. Tokens come from lib/theme only.
 */
export function MenuSelect({
    value,
    options,
    onChange,
    placeholder = "Select",
    readOnly,
    disabled,
    style
}: MenuSelectProps) {
    const [open, setOpen] = useState(false);
    const selected = options.find((o) => o.value === value);

    if (readOnly) {
        return (
            <View style={[styles.readOnly, style]}>
                <Text style={styles.readOnlyText}>{selected?.label ?? value ?? "—"}</Text>
            </View>
        );
    }

    return (
        <Menu
            visible={open}
            onDismiss={() => setOpen(false)}
            contentStyle={styles.menuContent}
            anchor={
                <Pressable
                    onPress={() => !disabled && setOpen(true)}
                    style={[
                        styles.anchor,
                        open && styles.anchorOpen,
                        disabled && styles.anchorDisabled,
                        style
                    ]}
                >
                    <Text
                        style={selected ? styles.valueText : styles.placeholderText}
                        numberOfLines={1}
                    >
                        {selected?.label ?? placeholder}
                    </Text>
                    <MaterialCommunityIcons
                        name={open ? "chevron-up" : "chevron-down"}
                        size={20}
                        color={colors.textTertiary}
                    />
                </Pressable>
            }
        >
            <ScrollView style={styles.menuScroll} keyboardShouldPersistTaps="handled">
                {options.map((opt) => {
                    const isSelected = opt.value === value;
                    return (
                        <Menu.Item
                            key={opt.value}
                            onPress={() => {
                                onChange(opt.value);
                                setOpen(false);
                            }}
                            title={opt.label}
                            titleStyle={isSelected ? styles.itemTitleSelected : styles.itemTitle}
                            trailingIcon={isSelected ? "check" : undefined}
                        />
                    );
                })}
            </ScrollView>
        </Menu>
    );
}

const styles = StyleSheet.create({
    anchor: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: spacing.sm,
        borderWidth: 1,
        borderColor: colors.surfaceBorder,
        borderRadius: radius.sm,
        paddingHorizontal: 14,
        paddingVertical: 12,
        backgroundColor: colors.background
    },
    anchorOpen: {
        borderColor: colors.primary
    },
    anchorDisabled: {
        backgroundColor: colors.backgroundSecondary
    },
    valueText: {
        ...typography.body,
        color: colors.textPrimary,
        flex: 1
    },
    placeholderText: {
        ...typography.body,
        color: colors.textTertiary,
        flex: 1
    },
    menuContent: {
        backgroundColor: colors.surface,
        borderRadius: radius.md
    },
    menuScroll: {
        maxHeight: MENU_MAX_HEIGHT
    },
    itemTitle: {
        ...typography.body,
        color: colors.textPrimary
    },
    itemTitleSelected: {
        ...typography.body,
        color: colors.teal600
    },
    readOnly: {
        borderWidth: 1,
        borderColor: colors.surfaceBorder,
        borderRadius: radius.sm,
        paddingHorizontal: 14,
        paddingVertical: 12,
        backgroundColor: colors.backgroundSecondary
    },
    readOnlyText: {
        ...typography.body,
        color: colors.textSecondary
    }
});
